/**
 * Sport Strategy Pattern
 * 
 * Encapsulates sport-specific validation, scoring, and UI rendering logic.
 * Each sport has its own strategy that defines:
 * - How scores are validated (ties allowed?, min/max values)
 * - How scores are displayed (sets vs points vs outcome)
 * - How match results are interpreted (winner determination)
 * 
 * Benefits:
 * - No if/else spaghetti in components
 * - Easy to add new sports
 * - Testable in isolation
 * - Type-safe with discriminated unions
 */

import type { Sport } from '@/types/api'

// ============================================================================
// Core Types
// ============================================================================

/**
 * Scoring system type determines how match results are recorded and validated
 */
export type ScoringSystem = 
  | 'SET_BASED'      // Best-of-N sets (table tennis, badminton, tennis)
  | 'GOAL_BASED'     // Goals/points count (football, hockey, basketball)
  | 'MATCH_OUTCOME'  // Win/Draw/Loss only (chess, some martial arts)

/**
 * Match outcome after score validation
 */
export type MatchOutcome = 
  | { type: 'WIN'; winnerId: string }
  | { type: 'DRAW' }

/**
 * Score validation result
 */
export type ScoreValidation = 
  | { valid: true; outcome: MatchOutcome }
  | { valid: false; errorKey: string } // i18n key like 'validation.noTieAllowed'

/**
 * Score display configuration for UI rendering
 */
export interface ScoreDisplayConfig {
  /** Input field label (e.g., "Set" for table tennis, "Poäng" for chess) */
  scoreLabel: string
  /** Placeholder text for score inputs */
  scorePlaceholder: string
  /** Min value for score input */
  minScore: number
  /** Max value for score input (undefined = no limit) */
  maxScore?: number
  /** Step value for score input (usually 1) */
  stepScore: number
  /** Whether to show "best of N" configuration */
  showBestOfConfig: boolean
  /** Help text explaining scoring rules */
  helpText?: string
}

/**
 * Sport-specific strategy interface
 */
export interface SportStrategy {
  /** Sport identifier */
  sport: Sport
  /** Scoring system type */
  scoringSystem: ScoringSystem
  /** Validates score and determines outcome */
  validateScore(scoreA: number, scoreB: number, context: ValidationContext): ScoreValidation
  /** Returns display configuration for match reporting UI */
  getScoreDisplay(): ScoreDisplayConfig
  /** Formats score for display (e.g., "3-2" or "1-0" or "½-½") */
  formatScore(scoreA: number, scoreB: number): string
}

/**
 * Context for score validation (format-specific rules)
 */
export interface ValidationContext {
  /** Best-of-N for set-based sports (3, 5, 7, etc.) */
  setsToPlay?: number
  /** Whether match is in knockout stage (no draws allowed) */
  isKnockout?: boolean
  /** Series format */
  seriesFormat?: string
}

// ============================================================================
// Strategy Implementations
// ============================================================================

/**
 * Table Tennis Strategy
 * - Set-based scoring (best of 3 or 5)
 * - No draws allowed
 * - Winner must reach (setsToPlay + 1) / 2 sets
 */
class TableTennisStrategy implements SportStrategy {
  sport: Sport = 'SPORT_TABLE_TENNIS'
  scoringSystem: ScoringSystem = 'SET_BASED'

  validateScore(scoreA: number, scoreB: number, context: ValidationContext): ScoreValidation {
    // No ties allowed in any format
    if (scoreA === scoreB) {
      return { valid: false, errorKey: 'validation.noTieAllowed' }
    }

    // Validate set requirements
    const setsToPlay = context.setsToPlay || 5
    const requiredSets = Math.floor((setsToPlay + 1) / 2)
    
    // At least one player must have reached requiredSets to win
    if (scoreA < requiredSets && scoreB < requiredSets) {
      return { valid: false, errorKey: 'validation.insufficientSets' }
    }

    // Winner cannot have more than requiredSets (match ends when someone reaches it)
    if (scoreA > requiredSets || scoreB > requiredSets) {
      return { valid: false, errorKey: 'validation.tooManySets' }
    }

    return {
      valid: true,
      outcome: { type: 'WIN', winnerId: scoreA > scoreB ? 'A' : 'B' }
    }
  }

  getScoreDisplay(): ScoreDisplayConfig {
    return {
      scoreLabel: 'matches.sets',
      scorePlaceholder: '0',
      minScore: 0,
      maxScore: 5,
      stepScore: 1,
      showBestOfConfig: true,
      helpText: 'validation.tableTennisHelp'
    }
  }

  formatScore(scoreA: number, scoreB: number): string {
    return `${scoreA}-${scoreB}`
  }
}

/**
 * Chess Strategy
 * - Match outcome scoring (1 point for win, 0.5 for draw, 0 for loss)
 * - Draws allowed in group stage
 * - Draws forbidden in knockout stage
 * - Score displayed as 1-0, ½-½, 0-1
 */
class ChessStrategy implements SportStrategy {
  sport: Sport = 'SPORT_CHESS'
  scoringSystem: ScoringSystem = 'MATCH_OUTCOME'

  validateScore(scoreA: number, scoreB: number, context: ValidationContext): ScoreValidation {
    // Chess uses 0 (loss), 0.5 (draw), 1 (win) but we store as integers
    // 0 = loss, 1 = draw (both get 0.5 points), 2 = win
    
    // Valid chess outcomes: 0-2 (loss-win), 1-1 (draw-draw), 2-0 (win-loss)
    const validOutcomes = [
      [0, 2], // A loses, B wins
      [1, 1], // Draw
      [2, 0]  // A wins, B loses
    ]

    const isValid = validOutcomes.some(([a, b]) => scoreA === a && scoreB === b)
    if (!isValid) {
      return { valid: false, errorKey: 'validation.invalidChessOutcome' }
    }

    // Draws forbidden in knockout
    if (context.isKnockout && scoreA === 1 && scoreB === 1) {
      return { valid: false, errorKey: 'validation.noDrawInKnockout' }
    }

    // Determine outcome
    if (scoreA === scoreB) {
      return { valid: true, outcome: { type: 'DRAW' } }
    }

    return {
      valid: true,
      outcome: { type: 'WIN', winnerId: scoreA > scoreB ? 'A' : 'B' }
    }
  }

  getScoreDisplay(): ScoreDisplayConfig {
    return {
      scoreLabel: 'matches.outcome',
      scorePlaceholder: '1',
      minScore: 0,
      maxScore: 2,
      stepScore: 1,
      showBestOfConfig: false,
      helpText: 'validation.chessHelp' // "0 = förlust, 1 = oavgjort, 2 = vinst"
    }
  }

  formatScore(scoreA: number, scoreB: number): string {
    // Format as chess notation: 1-0, ½-½, 0-1
    if (scoreA === 1 && scoreB === 1) {
      return '½-½'
    }
    if (scoreA === 2 && scoreB === 0) {
      return '1-0'
    }
    if (scoreA === 0 && scoreB === 2) {
      return '0-1'
    }
    return `${scoreA}-${scoreB}` // Fallback
  }
}

/**
 * Badminton Strategy
 * - Set-based scoring (best of 3)
 * - No draws allowed
 * - Similar to table tennis but typically best-of-3
 */
class BadmintonStrategy implements SportStrategy {
  sport: Sport = 'SPORT_BADMINTON'
  scoringSystem: ScoringSystem = 'SET_BASED'

  validateScore(scoreA: number, scoreB: number, context: ValidationContext): ScoreValidation {
    if (scoreA === scoreB) {
      return { valid: false, errorKey: 'validation.noTieAllowed' }
    }

    const setsToPlay = context.setsToPlay || 3
    const requiredSets = Math.floor((setsToPlay + 1) / 2)
    
    if (scoreA < requiredSets && scoreB < requiredSets) {
      return { valid: false, errorKey: 'validation.insufficientSets' }
    }

    return {
      valid: true,
      outcome: { type: 'WIN', winnerId: scoreA > scoreB ? 'A' : 'B' }
    }
  }

  getScoreDisplay(): ScoreDisplayConfig {
    return {
      scoreLabel: 'matches.sets',
      scorePlaceholder: '0',
      minScore: 0,
      maxScore: 3,
      stepScore: 1,
      showBestOfConfig: true
    }
  }

  formatScore(scoreA: number, scoreB: number): string {
    return `${scoreA}-${scoreB}`
  }
}

/**
 * Generic Goal-Based Strategy (Football, Hockey, Basketball)
 * - Goal/point count scoring
 * - Draws allowed in group stage
 * - Draws may require extra time/penalties in knockout
 */
class GoalBasedStrategy implements SportStrategy {
  sport: Sport
  scoringSystem: ScoringSystem = 'GOAL_BASED'

  constructor(sport: Sport) {
    this.sport = sport
  }

  validateScore(scoreA: number, scoreB: number, context: ValidationContext): ScoreValidation {
    // Draws forbidden in knockout (must play extra time/penalties)
    if (context.isKnockout && scoreA === scoreB) {
      return { valid: false, errorKey: 'validation.noDrawInKnockout' }
    }

    if (scoreA < 0 || scoreB < 0) {
      return { valid: false, errorKey: 'validation.negativeScore' }
    }

    if (scoreA === scoreB) {
      return { valid: true, outcome: { type: 'DRAW' } }
    }

    return {
      valid: true,
      outcome: { type: 'WIN', winnerId: scoreA > scoreB ? 'A' : 'B' }
    }
  }

  getScoreDisplay(): ScoreDisplayConfig {
    return {
      scoreLabel: 'matches.goals',
      scorePlaceholder: '0',
      minScore: 0,
      stepScore: 1,
      showBestOfConfig: false,
      helpText: 'validation.goalBasedHelp'
    }
  }

  formatScore(scoreA: number, scoreB: number): string {
    return `${scoreA}-${scoreB}`
  }
}

/**
 * Maximum score for goal-based sports (table hockey, foosball)
 * Note: Backend API validation currently limits scores to 0-5 for all sports.
 * Frontend validation uses this higher limit for better UX when manually entering scores.
 */
const MAX_GOAL_SCORE = 50

/**
 * Table Hockey Strategy (SBHF-compliant)
 * - Goal-based scoring (0-50 goals per match)
 * - Draws allowed in group stage
 * - Draws forbidden in knockout/playoffs
 */
class TableHockeyStrategy implements SportStrategy {
  sport: Sport = 'SPORT_TABLE_HOCKEY'
  scoringSystem: ScoringSystem = 'GOAL_BASED'

  validateScore(scoreA: number, scoreB: number, context: ValidationContext): ScoreValidation {
    // Score must be within valid range (0-MAX_GOAL_SCORE)
    if (scoreA < 0 || scoreB < 0) {
      return { valid: false, errorKey: 'validation.negativeScore' }
    }

    if (scoreA > MAX_GOAL_SCORE || scoreB > MAX_GOAL_SCORE) {
      return { valid: false, errorKey: 'validation.scoreTooHigh' }
    }

    // Draws forbidden in knockout/playoffs
    if (context.isKnockout && scoreA === scoreB) {
      return { valid: false, errorKey: 'validation.noDrawInKnockout' }
    }

    if (scoreA === scoreB) {
      return { valid: true, outcome: { type: 'DRAW' } }
    }

    return {
      valid: true,
      outcome: { type: 'WIN', winnerId: scoreA > scoreB ? 'A' : 'B' }
    }
  }

  getScoreDisplay(): ScoreDisplayConfig {
    return {
      scoreLabel: 'matches.goals',
      scorePlaceholder: '0',
      minScore: 0,
      maxScore: MAX_GOAL_SCORE,
      stepScore: 1,
      showBestOfConfig: false,
      helpText: 'validation.tableHockeyHelp'
    }
  }

  formatScore(scoreA: number, scoreB: number): string {
    return `${scoreA}-${scoreB}`
  }
}

/**
 * Foosball Strategy
 * - Goal-based scoring (same as table hockey)
 * - Draws allowed in group stage
 * - Draws forbidden in knockout/playoffs
 */
class FoosballStrategy implements SportStrategy {
  sport: Sport = 'SPORT_FOOSBALL'
  scoringSystem: ScoringSystem = 'GOAL_BASED'

  validateScore(scoreA: number, scoreB: number, context: ValidationContext): ScoreValidation {
    // Score must be within valid range (0-MAX_GOAL_SCORE)
    if (scoreA < 0 || scoreB < 0) {
      return { valid: false, errorKey: 'validation.negativeScore' }
    }

    if (scoreA > MAX_GOAL_SCORE || scoreB > MAX_GOAL_SCORE) {
      return { valid: false, errorKey: 'validation.scoreTooHigh' }
    }

    // Draws forbidden in knockout/playoffs
    if (context.isKnockout && scoreA === scoreB) {
      return { valid: false, errorKey: 'validation.noDrawInKnockout' }
    }

    if (scoreA === scoreB) {
      return { valid: true, outcome: { type: 'DRAW' } }
    }

    return {
      valid: true,
      outcome: { type: 'WIN', winnerId: scoreA > scoreB ? 'A' : 'B' }
    }
  }

  getScoreDisplay(): ScoreDisplayConfig {
    return {
      scoreLabel: 'matches.goals',
      scorePlaceholder: '0',
      minScore: 0,
      maxScore: MAX_GOAL_SCORE,
      stepScore: 1,
      showBestOfConfig: false,
      helpText: 'validation.foosballHelp'
    }
  }

  formatScore(scoreA: number, scoreB: number): string {
    return `${scoreA}-${scoreB}`
  }
}

// ============================================================================
// Strategy Registry
// ============================================================================

const strategyRegistry = new Map<Sport, SportStrategy>([
  ['SPORT_TABLE_TENNIS', new TableTennisStrategy()],
  ['SPORT_BADMINTON', new BadmintonStrategy()],
  ['SPORT_CHESS', new ChessStrategy()],
  ['SPORT_TENNIS', new TableTennisStrategy()], // Similar rules
  ['SPORT_PADEL', new TableTennisStrategy()],  // Similar rules
  ['SPORT_SQUASH', new TableTennisStrategy()], // Similar rules
  ['SPORT_PICKLEBALL', new TableTennisStrategy()], // Similar rules
  ['SPORT_TABLE_HOCKEY', new TableHockeyStrategy()],
  ['SPORT_FOOSBALL', new FoosballStrategy()]
])

/**
 * Get strategy for a specific sport
 */
export function getSportStrategy(sport: Sport): SportStrategy {
  const strategy = strategyRegistry.get(sport)
  if (!strategy) {
    throw new Error(`No strategy defined for sport: ${sport}`)
  }
  return strategy
}

/**
 * Check if sport allows draws in group stage
 */
export function allowsDrawsInGroupStage(sport: Sport): boolean {
  const strategy = getSportStrategy(sport)
  return strategy.scoringSystem === 'MATCH_OUTCOME' || strategy.scoringSystem === 'GOAL_BASED'
}

/**
 * Check if sport requires set configuration
 */
export function requiresSetConfiguration(sport: Sport): boolean {
  const strategy = getSportStrategy(sport)
  return strategy.scoringSystem === 'SET_BASED'
}
